import { ReactNode, memo, useCallback } from 'react';
import cn from 'classnames';

import styles from './CheckboxList.module.scss';

export interface FilterCheckboxItem {
  name: string;
  icon?: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

export interface FilterCheckboxListProps {
  items: FilterCheckboxItem[];
  className?: string;
}

// Memoized individual checkbox item to prevent unnecessary re-renders
const CheckboxItem = memo<FilterCheckboxItem>(({ name, icon, checked, onChange, disabled }) => {
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.checked);
  }, [onChange]);

  return (
    <label
      className={cn(styles.checkboxItem, {
        [styles.disabled]: disabled,
        [styles.checked]: checked,
      })}
    >
      <span className={styles.checkboxWrapper}>
        <input
          type="checkbox"
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          className={styles.checkboxInput}
        />
        <span className={styles.checkboxCustom} />
      </span>
      {icon && <span className={styles.icon}>{icon}</span>}
      <span className={styles.label}>{name}</span>
    </label>
  );
});

CheckboxItem.displayName = 'CheckboxItem';

export const CheckboxList = memo<FilterCheckboxListProps>(({
  items,
  className,
}) => {
  return (
    <div className={cn(styles.checkboxList, className)}>
      {items.map((item, index) => (
        <CheckboxItem
          key={`${item.name}-${index}`}
          name={item.name}
          icon={item.icon}
          checked={item.checked}
          onChange={item.onChange}
          disabled={item.disabled}
        />
      ))}
    </div>
  );
});

CheckboxList.displayName = 'CheckboxList';
